import type {MouseEvent as ReactMouseEvent} from 'react';
import {useState} from 'react';

import {useThemeColors} from '@nfq/colors';
import {useFormField} from '@nfq/react-form';

import {useOutsideClick} from '../../../hooks/useOutsideClick';
import {useSelectList} from '../hooks/useSelectList';

import type {Option} from '../../../../utils/childTypes';

/**
 * Custom hook that manages the state and behavior of a select input component with support for both single and multi-select modes.
 * This hook handles dropdown visibility, option selection, form field integration, and provides utilities for positioning and styling.
 * It integrates with the form field system and provides comprehensive event handlers for user interactions with the select component.
 *
 * @param name          The name identifier for the form field, used for form validation and field tracking.
 * @param initialValue  The initial value(s) for the select input, can be a single string or array of strings for multi-select.
 * @param options       Array of selectable options, each containing a label and value pair.
 * @param onChange      Callback function triggered when the selection changes, receives the new value(s) as parameter.
 * @param isDisabled    Boolean flag indicating whether the select input should be disabled and non-interactive.
 * @param isMultiSelect Boolean flag determining if the select allows multiple selections or single selection only.
 * @param keepOnUnmount Optional flag to preserve form field value when component unmounts, defaults to false.
 * @returns Object containing state values, event handlers, and utility properties for managing the select input.
 *
 * @example
 * ```tsx
 * const {
 *     color,
 *     handleChangeValue,
 *     handleOpenSelect,
 *     isOpen,
 *     value
 * } = useSelectInput(
 *     'country',
 *     'us',
 *     [
 *         { label: 'United States', value: 'us' },
 *         { label: 'Canada', value: 'ca' }
 *     ],
 *     (newValue) => console.log('Selected:', newValue),
 *     false,
 *     false
 * );
 * ```
 */
export const useSelectInput = (
    name: string,
    initialValue: string[] | string,
    options: Option[],
    onChange: (value: any) => void,
    isDisabled: boolean,
    isMultiSelect: boolean,
    keepOnUnmount = false
) => {
    const colors = useThemeColors();
    const {handleFieldChange, value} = useFormField(name, initialValue, [], undefined, keepOnUnmount);
    const [isOpen, setIsOpen] = useState(false);
    const selectRef = useOutsideClick<HTMLDivElement>(setIsOpen);
    const {isTop, listHeight} = useSelectList(selectRef, isOpen);
    const color = isOpen ? colors.primary : colors.textSecondary;

    /**
     * Handles the action of opening the select dropdown.
     * If the select input is disabled, the function returns early and does not open the dropdown.
     * Otherwise, it sets the dropdown to be open by updating the `isOpen` state to `true`.
     * This function is used to trigger the display of the options list when the select input is clicked.
     */
    const handleOpenSelect = () => {
        if (isDisabled) return;

        setIsOpen(true);
    };

    /**
     * Handles the action of selecting a new value from the dropdown options.
     * It stops the event propagation to prevent any parent click handlers from being triggered.
     * Then, it retrieves the selected value using the `index` stored in the dataset of the clicked option element.
     * Once the value is identified, it updates the state with the selected value, closes the dropdown, and triggers the `onChange` callback with the new value.
     *
     * @param event The mouse event triggered when an option is clicked.
     */
    const handleChangeValue = (event: ReactMouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
        const {index: fieldValue} = event.currentTarget.dataset;
        const realValue = options.filter(({value: optionValue}) => String(optionValue) === fieldValue)[0]?.value;

        if (isMultiSelect && Array.isArray(value)) {
            let newValue = Array.isArray(value) ? Array.from(value) : [];

            if (newValue.includes(realValue)) {
                newValue = value.filter(val => val !== realValue);
            } else {
                newValue.push(realValue);
            }
            handleFieldChange(newValue);
            onChange(newValue);
        } else {
            handleFieldChange(realValue);
            setIsOpen(false);
            onChange(realValue);
        }
    };

    return {
        color,
        handleChangeValue,
        handleOpenSelect,
        isOpen,
        isTop,
        listHeight,
        selectRef,
        value
    };
};